import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Plus, UserX, ArrowRight } from 'lucide-react';
import { useDataLabel } from '@/lib/dataI18n';

/**
 * Organizational Workspace card (UAT Finding 10 + Founder UAT A/G).
 * Shows live-derived membership plus the Phase 3 formal-positions summary
 * (authorized / filled / vacant seats — always derived, never manual).
 */
export default function OrgDepartmentCard({
  department: d,
  onOpenSheet,
  canManagePositions = false,
  onManagePositions,
  headerAction = null,
  testIdPrefix = 'org-dept',
}) {
  const { t } = useTranslation();
  const tData = useDataLabel();

  const members = d.members_preview || [];
  const count = d.member_count ?? members.length;
  const vacant = d.vacant_positions ?? 0;
  const manager = d.manager;

  return (
    <Card
      className="p-5 shadow-card hover:shadow-medium transition-smooth flex flex-col"
      data-testid={`${testIdPrefix}-card-${d.id}`}
    >
      {/* Header — icon + code badge + optional action (in-flow: never overlaps) */}
      <div className="flex items-start justify-between gap-2">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Users className="h-5 w-5" />
        </div>
        <div className="flex items-center gap-2 min-w-0">
          {d.code && <Badge variant="outline" className="text-xs shrink-0" data-testid={`${testIdPrefix}-code-${d.id}`}>{d.code}</Badge>}
          {headerAction}
        </div>
      </div>
      <h3 className="font-display text-lg font-bold tracking-tight mt-4" data-testid={`${testIdPrefix}-name-${d.id}`}>
        {tData(d)}
      </h3>
      <p className="text-sm text-muted-foreground mt-1" data-testid={`${testIdPrefix}-manager-${d.id}`}>
        {manager
          ? <>{t('pages.clientDepartments.org.manager', { defaultValue: 'Manager' })}: <span className="font-medium text-foreground">{manager.name}</span></>
          : <span className="italic">{t('pages.clientDepartments.org.noManager', { defaultValue: '— No manager assigned' })}</span>
        }
      </p>

      {/* Headline row — members · derived seats (Phase 3) */}
      <div className="mt-4 rounded-lg border bg-muted/30 px-3 py-2.5" data-testid={`${testIdPrefix}-headline-${d.id}`}>
        <div className="flex items-center justify-between text-sm">
          <div className="font-semibold tabular-nums">
            {t('pages.clientDepartments.org.members', { count, defaultValue: '{{count}} members' })}
          </div>
          <div className="text-xs text-muted-foreground tabular-nums" title={t('phase3.seats.derivedHint', { defaultValue: 'Vacant seats are always derived: authorized seats − filled seats.' })}>
            {(d.authorized_seats || 0) > 0
              ? t('phase3.seats.summary', { defaultValue: '{{filled}}/{{authorized}} seats · {{vacant}} vacant', filled: d.filled_seats || 0, authorized: d.authorized_seats || 0, vacant: vacant, count: vacant })
              : `${vacant} ${t('phase3.seats.vacantPlural', { defaultValue: 'vacant seats' })}`}
          </div>
        </div>
        <div className="flex items-center gap-1 mt-2">
          {members.slice(0, 5).map((m) => (
            <div
              key={m.id}
              title={m.name}
              className="h-6 w-6 rounded-full bg-primary/20 text-xs font-semibold text-primary flex items-center justify-center border border-background"
            >
              {m.avatar_initials || '—'}
            </div>
          ))}
          {count > 5 && (
            <span className="text-xs text-muted-foreground ml-1">+{count - 5} {t('pages.clientDepartments.org.more', { defaultValue: 'more' })}</span>
          )}
        </div>
      </div>

      {/* Members inline preview */}
      <div className="mt-4">
        <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
          {t('pages.clientDepartments.org.membersTitle', { defaultValue: 'Members' })}
        </p>
        {count === 0 ? (
          <button
            type="button"
            onClick={() => onOpenSheet?.(d)}
            className="w-full text-left rounded-lg border border-dashed border-border/70 px-3 py-4 text-sm text-muted-foreground italic hover:bg-muted/30 transition-smooth flex items-center gap-2"
            data-testid={`${testIdPrefix}-empty-${d.id}`}
          >
            <UserX className="h-4 w-4 shrink-0" />
            {t('pages.clientDepartments.org.emptyMembers', { defaultValue: 'No one is in this department yet.' })}
            <ArrowRight className="h-3.5 w-3.5 ml-auto" />
          </button>
        ) : (
          <ul className="space-y-1.5" data-testid={`${testIdPrefix}-preview-${d.id}`}>
            {members.map((m) => (
              <li
                key={m.id}
                className="flex items-center gap-2 text-sm rounded-md px-2 py-1.5 hover:bg-muted/40 transition-smooth"
                data-testid={`${testIdPrefix}-member-${m.id}`}
              >
                <div className="h-6 w-6 rounded-full bg-primary/15 text-xs font-semibold text-primary flex items-center justify-center shrink-0">
                  {m.avatar_initials || '—'}
                </div>
                <div className="flex-1 min-w-0 truncate">
                  <span className="font-medium truncate">{m.name}</span>
                  {m.position && <span className="text-muted-foreground"> · {m.position}</span>}
                </div>
              </li>
            ))}
          </ul>
        )}
        {count > 0 && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="mt-2 w-full h-8 text-xs justify-between"
            onClick={() => onOpenSheet?.(d)}
            data-testid={`${testIdPrefix}-show-all-${d.id}`}
          >
            <span>{t('pages.clientDepartments.org.showAll', { count, defaultValue: 'Show all {{count}} members' })}</span>
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Founder UAT (A) — Formal positions (Phase 3 canonical model):
          Person → Position → Role Profile. Replaces the legacy string
          catalogue accordion; the old catalogue now only feeds title
          suggestions inside the management dialog. */}
      <div className="mt-4 pt-4 border-t border-border/60" data-testid={`${testIdPrefix}-formal-positions-${d.id}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground inline-flex items-center gap-1"
                title={t('phase3.positionsClient.cardTip', { defaultValue: 'Formal positions link a person to a governed Role Profile. Vacancies are derived from authorized seats.' })}>
            {t('phase3.positionsClient.cardTitle', { defaultValue: 'Formal positions' })} ({d.formal_positions_count ?? 0})
          </span>
          {canManagePositions && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2.5 text-xs gap-1"
              onClick={() => onManagePositions?.(d)}
              data-testid={`${testIdPrefix}-manage-positions-${d.id}`}
            >
              <Plus className="h-3 w-3" />
              {t('phase3.positionsClient.manage', { defaultValue: 'Manage positions' })}
            </Button>
          )}
        </div>
        {(d.formal_positions_count ?? 0) === 0 ? (
          <p className="mt-1.5 text-xs text-muted-foreground" data-testid={`${testIdPrefix}-positions-empty-hint-${d.id}`}>
            {t('phase3.positionsClient.cardEmpty', { defaultValue: 'No formal positions yet — create one and link its Role Profile.' })}
          </p>
        ) : (
          <p className="mt-1.5 text-xs text-muted-foreground tabular-nums">
            {(d.authorized_seats || 0) > 0
              ? t('phase3.seats.summary', { defaultValue: '{{filled}}/{{authorized}} seats · {{vacant}} vacant', filled: d.filled_seats || 0, authorized: d.authorized_seats || 0, vacant: d.vacant_positions || 0, count: d.vacant_positions || 0 })
              : `${d.filled_seats || 0} ${t('phase3.seats.filledPlural', { defaultValue: 'filled seats' })}`}
          </p>
        )}
      </div>
    </Card>
  );
}
